import React, { useMemo } from 'react';
import { Body1 } from '@xo-union/tk-ui-typography';

import { varCoolgray200, varStatusSuccess200, varNeutralBlack, varNeutral200 } from '../../helpers/chartColors';

import { Gds_EventRsvpData } from '../../generated/graphql';
import styles from './rsvpSummaryCard.scss';
import Chart from './RsvpChart';
import { Badge, ChartLabel } from './utils';

interface IRsvpStatsStructure {
  color: string;
  label: string;
  count: number;
}

interface Props {
  data: Gds_EventRsvpData | undefined;
  isPrivateRsvp?: boolean;
}

const displayRsvpStats = (rsvpStatsStructure: IRsvpStatsStructure[]) =>
  rsvpStatsStructure.map((structure, idx) => {
    const { color, label, count } = structure;

    return (
      <div key={idx} className={styles.option}>
        <Badge color={color} />
        <div className={styles.label}>{label}</div>
        <div className={styles.count}>{count}</div>
      </div>
    );
  });

const RsvpDataResponseCard: React.FC<Props> = ({ data, isPrivateRsvp }) => {
  const guestCount = data?.guestCount || 0;
  const acceptCount = data?.acceptCount || 0;
  const rejectCount = data?.rejectCount || 0;
  const responses = acceptCount + rejectCount;

  const rsvpData = useMemo(() => {
    const rsvpStatsStructure = [
      { color: varStatusSuccess200, label: 'Accepted', count: acceptCount },
      { color: varNeutralBlack, label: 'Declined', count: rejectCount },
    ];

    // include no responses when rsvp is private
    if (isPrivateRsvp) {
      rsvpStatsStructure.push({
        color: varCoolgray200,
        label: 'No Response',
        count: guestCount - (acceptCount + rejectCount),
      });
    }

    return rsvpStatsStructure;
  }, [acceptCount, rejectCount, isPrivateRsvp, guestCount]);

  const optionColors = useMemo(() => {
    return isPrivateRsvp
      ? [varStatusSuccess200, varNeutralBlack, varNeutral200]
      : [varStatusSuccess200, varNeutralBlack];
  }, [isPrivateRsvp]);

  const allResponded = useMemo(
    () => (isPrivateRsvp && guestCount ? !(guestCount - (acceptCount + rejectCount)) : false),
    [guestCount, acceptCount, rejectCount, isPrivateRsvp]
  );

  if (typeof data === 'undefined') return null;
  return (
    <div className={styles.optionAnswerCardContainer}>
      <Body1 bold className={styles.question}>
        Will you be attending?
      </Body1>
      <Chart id={`chart-${data.eventId}`} data={rsvpData} label="label" value="count" colors={optionColors}>
        <ChartLabel
          allResponded={allResponded}
          isPrivateRsvp={isPrivateRsvp}
          responses={responses}
          total={guestCount}
        />
      </Chart>

      <div className={styles.responses}>
        <div className={styles.optionsContainer}>{displayRsvpStats(rsvpData)}</div>
      </div>
    </div>
  );
};

export default RsvpDataResponseCard;
